import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { isPreviewSite } from 'src/lib/js/previewModeUtils';
import { useStaticSiteContext } from 'src/public/components/app/StaticSiteContext';
import { extractAdsPixelMeta } from 'src/shared/components/common/ads_manager/adsManagerConverions';

import { getImageUrl } from 'shared/components/common/Image';
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext';

import { StructuredDataConsumer } from 'public/components/seo/context';

import { resources } from 'config';

import PixelsMeta, { PixelsMetaProps } from './PixelsMeta';
import StyleMeta, { StyleMetaProps } from './StyleMeta';
import UserWayMeta, { UserWayMetaProps } from './UserWayMeta';

type Props = {
  description?: string;
  icon: string;
  favicon?: string;
  logo?: string;
  heroImgSrc?: string
  title?: string | null;
  domain: string;
  titleTag?: string;
  noIndex?: boolean;
} & StyleMetaProps &
PixelsMetaProps &
UserWayMetaProps;

// Meta does not depend on RestaurantContext since it is shared with Toast Local.
// Toast Local shouldn't have site-specific data included.
// If you want site-specific data in <head>, use SiteMeta.
const Meta = (meta: Props) => {
  const location = useLocation();
  const url = `https://${meta.domain}${location.pathname}`;
  const title = `${meta.title}${meta.titleTag ? ` | ${meta.titleTag}` : ''}`;
  const logoSrc = meta.favicon || meta.icon || meta.logo;
  const ogImgSrc = meta.heroImgSrc || logoSrc;
  const externalModules = meta.externalModules;

  const isNotProduction = process.env.NODE_ENV !== 'production';
  // dont index if in a non-prod env
  const noIndex = isNotProduction || meta.noIndex;

  return (
    <>
      <Helmet>
        {noIndex ? <meta name="robots" content="noindex" /> : null}
        {meta.description ? <meta name="description" content={meta.description} /> : null}
        {meta.description ? <meta name="og:description" content={meta.description} /> : null}
        <link rel="canonical" href={url} />
        <meta name="og:url" content={url} />
        {title ? <title>{title}</title> : null}
        {title ? <meta name="og:title" content={title} /> : null}
        {ogImgSrc ? <meta name="og:image" content={getImageUrl(ogImgSrc, resources.imagePrefix)} /> : null}
        {logoSrc ? <link rel="icon" id="favicon" href={getImageUrl(logoSrc)} /> : null}
      </Helmet>
      <StructuredDataConsumer />
      <PixelsMeta pixels={meta.pixels} adsPixels={meta.adsPixels} />
      <StyleMeta {...meta} />
      {externalModules &&
        <>
          <UserWayMeta externalModules={externalModules} />
        </>}
    </>
  );
};

// Meta with site-specific data
export const SiteMeta = (meta: Props) => {
  const { restaurant, ooRestaurant, selectedLocation, toastProduct } = useRestaurant();
  const adsPixel = extractAdsPixelMeta(ooRestaurant?.adsManagerConversionTagsConfig);

  // don't index a site in preview mode or oo-only sites that dont have the OO module
  const staticContext = useStaticSiteContext();
  const isPreviewMode = isPreviewSite(staticContext, restaurant?.config, toastProduct);
  const ooOnly = restaurant.config.isOnlineOrderingOnly ?? true;
  const hasOOModule = ooRestaurant?.hasOnlineOrderingModule ?? true;
  const noIndex = isPreviewMode || ooOnly && !hasOOModule;

  return <Meta
    {...meta}
    noIndex={noIndex}
    adsPixels={adsPixel}
    title={meta.title || selectedLocation?.name}
    logo={ooRestaurant?.logoUrls?.small}
    heroImgSrc={restaurant.config.ooConfig?.heroImage?.displaySrc} />;
};

export default Meta;
